import '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanning.scss';
import cn from 'classnames';
import {
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdArrowForwardIos, MdEdit, MdWarning } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { ScenarioPlanningModelType } from '@blisspointmedia/bpm-types/dist/ScenarioPlanning';
import { AlertDialog } from '@Components/AlertDialog/AlertDialog';
import { Button } from '@Components/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { DcDgBadge } from '@Components/DcDgBadge/DcDgBadge';
import {
  Appearance,
  Design,
  Intent,
  Placement,
  Size,
  StretchMode
} from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { ShadedPattern } from '@Components/FillPatterns/ShadedPattern';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { IconButton } from '@Components/IconButton';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { buildChannelColorMap } from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import {
  CHART_SCROLL_AMOUNT,
  MAX_BAR_WIDTH,
  MIN_BAR_GAP,
  ScenarioPlanningChart,
  ScenarioPlanningChartData,
  Y_AXIS_WIDTH
} from '@Components/ScenarioPlanningChart/ScenarioPlanningChart';
import { SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS } from '@Components/SpendAndEffectModal/SpendAndEffectGlossary';
import { TableEmptyState } from '@Components/TableEmptyState/TableEmptyState';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { EDITOR_USER_GROUPS } from '@Pages/FeatureExperiments/useCanEditExperiments';
import { PLAN_OPTIONS } from '@Pages/GeoMMM/ScenarioPlanning/Modals/AdjustModal';
import { ChangeImpactModal } from '@Pages/GeoMMM/ScenarioPlanning/Modals/ChangeImpactModal';
import { EditScenarioModal } from '@Pages/GeoMMM/ScenarioPlanning/Modals/EditScenarioModal';
import { RunScenarioProgressDialog } from '@Pages/GeoMMM/ScenarioPlanning/Modals/RunScenarioProgressDialog';
import { exportScenarioPlanningData } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningExportUtils';
import {
  AnalyzeScenarioResult,
  BaselineData,
  CometProgress,
  getBrandEquityBaselineScenario,
  getGeoMMMBaselineScenario,
  getHROASBaselineScenarioLambda,
  getRMMMBaselineScenario,
  HROASModelConfig,
  MmmFeatureNameMap,
  ScenarioPlanningModelExt
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningLambdaUtils';
import {
  adjustScenario,
  analyzeScenario,
  buildHroasAnalyzeNotices,
  buildScenarioKey,
  computeBaselineData,
  countRevenueRowsWithoutSpendSource,
  capitalizeNames,
  CHART_SORT_BY_ENUM,
  CHART_SORT_BY_OPTIONS,
  DIMENSIONS,
  filterHroasNotesForViewer,
  getActualizedMetricValue,
  getChartLabel,
  getChartSortOptionLabel,
  getCostMetricLabel,
  getEffectLabel,
  getGroupKey,
  getHroasFailedModelIds,
  getMetricValue,
  getModelCacheKey,
  getModelRevenueWeight,
  getModelSegments,
  getScenarioKeyBase,
  getScenarioMetricLabel,
  GROUP_BY_OPTIONS,
  isLegacyRmmmRun,
  isNA,
  isSpDebug,
  mergeRowIntoModelRevenue,
  MODEL_TYPES,
  ModelConfig,
  modelRunKey,
  normalizeModelWeights,
  PairwiseInteraction,
  pickDefaultModel,
  ROW_MODEL_TYPE_TO_ENUM,
  SCENARIO_METRIC_ENUM,
  ScenarioDateRow,
  ScenarioGranularityEnum,
  ScenarioNode,
  scenarioRedshiftDataToTree,
  sliceBaselineRows,
  SORT_DIRECTION_ENUM,
  SORT_DIRECTION_LABEL_MAP,
  SORT_DIRECTION_OPTIONS,
  stripDcDgMarker,
  sumWeightedByModel,
  toHroasRunArgs,
  updateTreeWithAnalyzedData,
  HroasDisplayWarning,
  HroasRunSelection,
  HroasSpModel
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import {
  ScenarioSparkCharts,
  SparkChartCardProps
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioSparkCharts';
import { useScenarioPlanningToolBar } from '@Pages/GeoMMM/ScenarioPlanning/UseScenarioPlanningToolBar';
import { useHroasWarningDialog } from '@Pages/GeoMMM/ScenarioPlanning/useHroasWarningDialog';
import { EditModelWeightingModal } from '@Pages/HROAS/EditModelWeightingModal';
import { ModelConfig as HroasWeightingModelConfig } from '@Pages/HROAS/hroas-types';
import { useUserGroups } from '@src/utils/hooks/useUserGroups';
import { useSetError } from '../../../redux/modals';
import { isInternalSelector } from '../../../redux/user';
import { Brand70, getModelShade } from '../../../utils/colors';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '../../../utils/mixpanelWrapper';
import { applyFeatureMappingToRow, resolveFeatureParts } from '../../../utils/mmm-feature-mapping';

// All three model types are served dynamically: each run row from the page's run list carries its
// scenario model, gated per-run by Postgres + S3. BrandEquity pairs a stage-one
// (brand-metric) and stage-two (revenue) Meridian model joined by their shared run timestamp.
const usesDynamicModels = (modelType: string): boolean =>
  modelType === MODEL_TYPES.GEO_MMM ||
  modelType === MODEL_TYPES.R_MMM ||
  modelType === MODEL_TYPES.BRAND_EQUITY;

// All model types read their S3 keys from the selected run (carried on the page's run rows).
const getModelConfig = (
  selectedModel: ScenarioPlanningModelExt | undefined
): ModelConfig | undefined => {
  if (selectedModel?.modelType === ScenarioPlanningModelType.BrandEquity) {
    return {
      modelType: MODEL_TYPES.BRAND_EQUITY,
      stageOneModels: selectedModel.stageOneModels,
      stageTwoModelS3Key: selectedModel.stageTwoModelS3Key
    };
  }
  if (selectedModel?.modelType === ScenarioPlanningModelType.RMMM) {
    const { fxzModelS3Key, configS3Key, clientDataS3Key, gxWorkspaceS3Key, gxTracesS3Keys } =
      selectedModel;
    // A legacy (pre-Meridian) run has none of these — it's not offered in the picker, so this
    // should be unreachable in practice, but never build a config from S3 keys that may not
    // exist rather than trust hasScenarioArtifacts/isLegacyRmmmRun alone.
    if (!fxzModelS3Key || !configS3Key || !clientDataS3Key || !gxWorkspaceS3Key) {
      return undefined;
    }
    return {
      modelType: MODEL_TYPES.R_MMM,
      fxzModelS3Key,
      configS3Key,
      clientDataS3Key,
      gxWorkspaceS3Key,
      // Omitted by the backend — the python rMMM lambda auto-discovers gx_trace_*.nc under the
      // workspace prefix when this is empty.
      gxTracesS3Keys: gxTracesS3Keys ?? []
    };
  }
  if (selectedModel?.modelType === ScenarioPlanningModelType.GeoMMM) {
    return {
      modelType: MODEL_TYPES.GEO_MMM,
      modelS3Key: selectedModel.s3Key
    };
  }
  return undefined;
};

// Replace opaque feature ids in the baseline rows with pretty channel/platform/tactic for display,
// preserving the originals on rawChannel/rawPlatform/rawTactic so the analyze payload (built from
// the tree's refIds) still uses the original feature id. A pure helper (like computeBaselineData) so
// the React Compiler memoizes the call and every consumer stays reactive to the async featureMapping.
// Rows already showing readable names are left untouched.
//
// ==== HROAS (unmigrated): blends rMMM + BrandEquity rows (each carrying its own row.modelType) in
// one bundle, so a single featureMapping isn't enough — featureMaps[row.modelType] resolves each row
// against ITS OWN model's map (mirrors ModelResults' resolveFeatureRowMultiMap). Falls back to the
// single featureMapping when featureMaps has no entry for that modelType, so single-model callers
// (GeoMMM/BrandEquity/MmmVariantPage, which never pass featureMaps) are unaffected. ====
const normalizeBaselineData = (
  data: BaselineData | undefined,
  featureMapping?: MmmFeatureNameMap,
  featureMaps?: Record<string, MmmFeatureNameMap | undefined>
): BaselineData | undefined => {
  const mapForRow = (row: { modelType?: string }): MmmFeatureNameMap | undefined =>
    featureMaps?.[row.modelType ?? ''] ?? featureMapping;
  return (
    data && {
      ...data,
      baselineSpend: {
        ...data.baselineSpend,
        rows: data.baselineSpend.rows.map(row => applyFeatureMappingToRow(row, mapForRow(row)))
      },
      baselineRevenue: {
        ...data.baselineRevenue,
        rows: data.baselineRevenue.rows.map(row => applyFeatureMappingToRow(row, mapForRow(row)))
      }
    }
  );
};

// Rows with NaN direct spend/revenue are skipped during aggregation.
const hasValidDirects = (directSpend: number, directRevenue: number): boolean =>
  !Number.isNaN(directSpend) && !Number.isNaN(directRevenue);

// Pure helper so the React Compiler can memoize the call (matches computeBaselineData above).
// An included HROAS model with no rows in the date-filtered baselineData isn't a load failure
// (that's rawBaselineData.warnings) — it's a data-coverage gap for this specific window.
const computeHroasNoDataModelIds = (
  isHroas: boolean,
  rawBaselineData: BaselineData | undefined,
  baselineData: ScenarioDateRow[],
  includedModelIds: Set<string>,
  hroasModels: HroasSpModel[]
): string[] => {
  if (!isHroas || !rawBaselineData?.baselineSpend?.rows?.length) {
    return [];
  }
  const loadFailedIds = new Set((rawBaselineData.warnings ?? []).map(w => w.modelId));
  const presentIds = new Set(baselineData.map(row => row.modelId));
  return hroasModels
    .filter(
      model =>
        includedModelIds.has(model.id) && !loadFailedIds.has(model.id) && !presentIds.has(model.id)
    )
    .map(model => model.id);
};

// Pure helper so the React Compiler can memoize the call (matches computeBaselineData above).
// Per-model date coverage, mirroring the union scan in the toolbar-bounds effect below but grouped
// by modelId instead of scanning across every model at once. HROAS-only — non-HROAS rows never
// carry modelId, so this is a no-op for every other model type.
const computeHroasModelDateBounds = (
  isHroas: boolean,
  rawBaselineData: BaselineData | undefined
): Record<string, { minDate: string; maxDate: string }> => {
  if (!isHroas) {
    return {};
  }
  const spendRows = rawBaselineData?.baselineSpend?.rows ?? [];
  const revenueRows = rawBaselineData?.baselineRevenue?.rows ?? [];
  const boundsMs: Record<
    string,
    { minDate: string; maxDate: string; minMs: number; maxMs: number }
  > = {};
  const len = Math.min(spendRows.length, revenueRows.length);
  for (let i = 0; i < len; i++) {
    if (!revenueRows[i]) {
      continue;
    }
    const { week, modelId } = spendRows[i];
    if (!modelId) {
      continue;
    }
    const rowMs = new Date(week).getTime();
    const existing = boundsMs[modelId];
    if (!existing) {
      boundsMs[modelId] = { minDate: week, maxDate: week, minMs: rowMs, maxMs: rowMs };
      continue;
    }
    if (rowMs < existing.minMs) {
      existing.minMs = rowMs;
      existing.minDate = week;
    }
    if (rowMs > existing.maxMs) {
      existing.maxMs = rowMs;
      existing.maxDate = week;
    }
  }
  return Object.fromEntries(
    Object.entries(boundsMs).map(([modelId, b]) => [
      modelId,
      { minDate: b.minDate, maxDate: b.maxDate }
    ])
  );
};

interface HroasDateIntersection {
  intersectionStart: string | undefined;
  intersectionEnd: string | undefined;
  hasOverlap: boolean;
  unionStart: string | undefined;
  unionEnd: string | undefined;
}

// PRD §6.2: the toolbar's pickable range must be the INTERSECTION of only the currently-included
// models' own date coverage (max of their mins, min of their maxes) — not the union of every row
// in rawBaselineData, which merges all models together and finds the widest possible range instead
// of the narrowest common one. Also returns the union of the same included models as a fallback for
// the no-overlap edge case (see the caller in the toolbar-bounds effect).
const computeHroasIncludedDateIntersection = (
  isHroas: boolean,
  modelDateBounds: Record<string, { minDate: string; maxDate: string }>,
  includedModelIds: Set<string>
): HroasDateIntersection => {
  const emptyResult: HroasDateIntersection = {
    intersectionStart: undefined,
    intersectionEnd: undefined,
    hasOverlap: true,
    unionStart: undefined,
    unionEnd: undefined
  };
  if (!isHroas) {
    return emptyResult;
  }
  const includedBounds = Object.entries(modelDateBounds)
    .filter(([modelId]) => includedModelIds.has(modelId))
    .map(([, bounds]) => bounds);
  if (includedBounds.length === 0) {
    return emptyResult;
  }
  const intersectionStart = includedBounds.reduce(
    (latest, b) => (new Date(b.minDate) > new Date(latest) ? b.minDate : latest),
    includedBounds[0].minDate
  );
  const intersectionEnd = includedBounds.reduce(
    (earliest, b) => (new Date(b.maxDate) < new Date(earliest) ? b.maxDate : earliest),
    includedBounds[0].maxDate
  );
  const unionStart = includedBounds.reduce(
    (earliest, b) => (new Date(b.minDate) < new Date(earliest) ? b.minDate : earliest),
    includedBounds[0].minDate
  );
  const unionEnd = includedBounds.reduce(
    (latest, b) => (new Date(b.maxDate) > new Date(latest) ? b.maxDate : latest),
    includedBounds[0].maxDate
  );
  return {
    intersectionStart,
    intersectionEnd,
    hasOverlap: new Date(intersectionStart) <= new Date(intersectionEnd),
    unionStart,
    unionEnd
  };
};

// Demand-capture/-generation suffix + hierarchical key builder for the flighting map.
const dcSuffixFor = (node: ScenarioNode): string => {
  if (node.isDemandCapture === true) return ', dc';
  if (node.isDemandCapture === false) return ', dg';
  return '';
};
const keyFor = (prefix: string, formattedName: string): string => {
  if (!prefix) return formattedName;
  return prefix.endsWith(formattedName) ? prefix : `${prefix}, ${formattedName}`;
};

// Demand-capture / demand-generation legend entries (rendered via LegendItem).
const DC_DG_LEGEND_ITEMS: { term: string; label: string; isDemandCapture: boolean }[] = [
  { term: 'Demand Capture', label: 'Demand Capture Channel', isDemandCapture: true },
  { term: 'Demand Generation', label: 'Demand Generation Channel', isDemandCapture: false }
];

// Floor for the chart container width. The container is shrunk to fit its bars (to keep margins
// when few bars are shown), but it must stay wide enough that the header — title, search/filter
// bar (GeoMMM/BrandEquity only, see searchBar/rightActions), sort/group dropdowns, edit button —
// doesn't wrap or overlap. See chartContainerMaxWidth. Left generously sized rather than tuned
// tight, since the shared .main-header wrap-safety CSS is still the real backstop if any
// real-world width (title text, long dropdown labels) ever exceeds it.
const MIN_CHART_CONTAINER_WIDTH = 860;
// HROAS's title is longer than every other model type's, so it needs a taller base floor than the
// generic MIN_CHART_CONTAINER_WIDTH above — scoped to HROAS only so GeoMMM/rMMM/BrandEquity's
// container sizing stays byte-for-byte unchanged.
const HROAS_MIN_CHART_CONTAINER_WIDTH = 1000;

// ==== rMMM/HROAS: the Actualized/Absolute dropdowns (showRmmmControls), which still live in the
// header, add width the base MIN_CHART_CONTAINER_WIDTH floor above doesn't cover. 0 for
// GeoMMM/BrandEquity, whose shorter toolbar is already covered by the base floor above. ====
const RMMM_CONTROLS_EXTRA_WIDTH = 360;

// ==== HROAS: the model-selection-bar/legend-bar add width the generic MIN_CHART_CONTAINER_WIDTH
// floor above was never tuned for (they don't exist on non-HROAS pages) — scale an additive floor
// with the number of configured models instead of guessing a flat number. Estimates, not measured
// (see ScenarioPlanning.scss's chip/bar styles) — re-tune if a run has many more slots than today's
// handful or labels get much longer. Steps 2/4's wrap-safety CSS is the backstop either way. ====
const HROAS_MODEL_CHIP_WIDTH_ESTIMATE = 330;
const HROAS_BAR_CHROME_WIDTH = 170;

interface ScenarioPlanningProps {
  modelType: string;
  // Structured feature map (from useFeatureMapping); used to replace opaque feature ids.
  featureMapping?: MmmFeatureNameMap;
  // ==== HROAS (unmigrated): per-modelType feature maps (from useFeatureMapsByModelType), keyed
  // 'rMMM'/'GeoMMM'/'BrandEquity' — HROAS blends rows from multiple models in one bundle, so
  // featureMapping alone isn't enough. See normalizeBaselineData. ====
  featureMaps?: Record<string, MmmFeatureNameMap | undefined>;
  // ==== HROAS: the selected run (picked in the HROAS page header) and its trained model slots.
  // The slots drive the model chips/weights; the backend resolves S3 keys from the run. ====
  hroasRun?: HroasRunSelection;
  // The page's selected run (picked in the page's own run selector, shared with its Results tab).
  // `model` is that run's scenario model, delivered on the run row itself; a run without one shows
  // an unavailable state.
  sharedRun?: { model?: ScenarioPlanningModelExt };
  // Every run's scenario model, from the same run rows the picker lists.
  runModels?: ScenarioPlanningModelExt[];
}

export const ScenarioPlanning = ({
  modelType,
  featureMapping,
  featureMaps,
  hroasRun,
  sharedRun,
  runModels
}: ScenarioPlanningProps): ReactElement => {
  const [chartVariables, setChartVariables] = useState<SCENARIO_METRIC_ENUM[]>([
    SCENARIO_METRIC_ENUM.SPEND,
    SCENARIO_METRIC_ENUM.REVENUE
  ]);
  const [currentScenarioDataMap, setCurrentScenarioDataMap] =
    useState<Record<string, ScenarioDateRow>>();
  const [filter, setFilter] = useState<{ filter: (line: ScenarioDateRow) => boolean }>();
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [pageProgressPercent, setPageProgressPercent] = useState<number | null>(null);
  const [isRevenueData, setIsRevenueData] = useState<boolean>();
  const [maxEnd, setMaxEnd] = useState<string>();
  const [minStart, setMinStart] = useState<string>();
  const [newScenarioTree, setNewScenarioTree] = useState<ScenarioNode[]>();
  const [rawBaselineDataMap, setRawBaselineDataMap] = useState<Record<string, BaselineData>>({});
  // getModelConfig (below) returns a new object every render, so `modelConfig` is never
  // referentially stable across renders — including it in the baseline-fetch effect's deps
  // (needed for its values, e.g. modelConfig.modelS3Key) means that effect re-fires on every
  // render while a fetch is in flight (e.g. each comet progress tick re-renders the page). This
  // ref makes the fetch idempotent per model regardless of how often the effect re-fires.
  const inFlightModelCacheKeyRef = useRef<string | null>(null);
  const [scenarioPlanningModels, setScenarioPlanningModels] =
    useState<ScenarioPlanningModelExt[]>();
  const [selectedModelKey, setSelectedModelKey] = useState<string>();
  const [sortBy, setSortBy] = useState<CHART_SORT_BY_ENUM>(CHART_SORT_BY_ENUM.ALPHABETICALLY);
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTION_ENUM>(
    SORT_DIRECTION_ENUM.LOW_TO_HIGH
  );
  const [showChangeImpactModal, setShowChangeImpactModal] = useState<boolean>(false);
  const [oldScenarioSnapshot, setOldScenarioSnapshot] = useState<ScenarioNode[]>([]);
  const [isFirstScenarioRun, setIsFirstScenarioRun] = useState<boolean>(true);
  // The raw (pre-processed) response from the last successful analyzeScenario call — kept
  // separately from currentScenarioDataMap so the "Export data" download can include it verbatim.
  const [lastAnalyzeRawResult, setLastAnalyzeRawResult] = useState<AnalyzeScenarioResult>();
  const [showEditScenarioModal, setShowEditScenarioModal] = useState<boolean>(false);
  // ==== HROAS (unmigrated): the direct/indirect + Actualized controls apply to HROAS too — a
  // holistic view can contain rMMM-type instances (per-bar breakout is gated per-row in barChartData;
  // these controls are the global view toggles). ====
  const showRmmmControls = modelType === MODEL_TYPES.R_MMM || modelType === MODEL_TYPES.HROAS;
  // ==== HROAS: per-instance include/exclude + relative weight for the holistic blend (PRD
  // §5.1/§5.5 — toggling/reweighting must update every number instantly, with no lambda re-call).
  // Operates over the selected run's model slots. ====
  const isHroas = modelType === MODEL_TYPES.HROAS;
  // The run's slots, in run order. The page remounts this component per run (key=runId in
  // HROAS.tsx), so state initialised from it never goes stale.
  const hroasModels = useMemo<HroasSpModel[]>(
    () => (hroasRun?.slots ?? []).map(slot => ({ id: slot.slotId, label: slot.label })),
    [hroasRun?.slots]
  );
  const [includedModelIds, setIncludedModelIds] = useState<Set<string>>(
    () => new Set(hroasModels.map(m => m.id))
  );
  const [modelWeights, setModelWeights] = useState<Record<string, number>>(() =>
    Object.fromEntries(hroasModels.map(m => [m.id, 1]))
  );
  // ==== HROAS: normalized so only relative proportions matter (PRD §5.1 — "1·1·1·1 gives the
  // same result as 2·2·2·2"); this is what getModelRevenueWeight/sumWeightedByModel consume, not
  // the raw modelWeights state above. ====
  const normalizedModelWeights = useMemo(
    () => normalizeModelWeights(modelWeights, includedModelIds),
    [modelWeights, includedModelIds]
  );
  // ==== HROAS: real weighting dialog (PRD §6.1 — "a control to open the weighting dialog"),
  // reusing HROAS's existing EditModelWeightingModal pattern rather than a raw number input.
  // Shimmed into ModelConfig's shape (appLabel/enabled aren't meaningful concepts for Scenario
  // Planning's session-scoped weights, so they're filled with the closest equivalent) rather than
  // widening ModelConfig or EditModelWeightingModal's types — keeps the Model Results page's own
  // usage of both completely untouched. ====
  const [showWeightingModal, setShowWeightingModal] = useState<boolean>(false);
  // Memoized so its reference is stable across unrelated re-renders while EditModelWeightingModal
  // is open — that modal resets its in-progress edits whenever this reference changes.
  const weightingModels: HroasWeightingModelConfig[] = useMemo(
    () =>
      hroasModels.map(model => ({
        id: model.id,
        label: model.label,
        appLabel: model.label,
        enabled: includedModelIds.has(model.id),
        weight: modelWeights[model.id] ?? 1
      })),
    [hroasModels, includedModelIds, modelWeights]
  );
  const handleWeightingSave = (updated: HroasWeightingModelConfig[]): void => {
    setModelWeights(prev => ({
      ...prev,
      ...Object.fromEntries(updated.map(model => [model.id, model.weight]))
    }));
  };
  const [showActualized, setShowActualized] = useState<boolean>(showRmmmControls);
  const [showCrossChannelInteractions, setShowCrossChannelInteractions] = useState<boolean>(false);
  // ==== HROAS: per-model revenue segments (default on). Mutually exclusive with Cross-channel
  // Interactions, which already splits the bar into direct/indirect. ====
  const [showModelSegments, setShowModelSegments] = useState<boolean>(true);
  // Both views: a model with no direct revenue (e.g. brand equity) keeps its row in the tooltip at
  // $0 and takes no height in the Direct stack.
  const segmentsActive = isHroas && showModelSegments && !showCrossChannelInteractions;
  const handleCrossChannelChange = (checked: boolean): void => {
    setShowCrossChannelInteractions(checked);
    if (checked) {
      setShowModelSegments(false);
    }
  };
  const handleModelSegmentsChange = (checked: boolean): void => {
    setShowModelSegments(checked);
    if (checked) {
      setShowCrossChannelInteractions(false);
    }
  };
  // Enabled models in slot order; see segmentModelOrder in ScenarioPlanningChart.
  const segmentModelOrder = useMemo(
    () => hroasModels.filter(m => includedModelIds.has(m.id)).map(m => m.id),
    [hroasModels, includedModelIds]
  );
  const [showPercentageView, setShowPercentageView] = useState<boolean>(false);
  // TODO: pairwiseData will be consumed in a follow-up PR for rMMM pairwise visualization
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pairwiseData, setPairwiseData] = useState<PairwiseInteraction[]>([]);
  const setError = useSetError();
  const chartsColumnRef = useRef<HTMLDivElement>(null);
  const [chartsColumnWidth, setChartsColumnWidth] = useState(0);
  const [canScrollCharts, setCanScrollCharts] = useState(false);
  const [scrollAtStart, setScrollAtStart] = useState(true);
  const [scrollAtEnd, setScrollAtEnd] = useState(false);
  const companyInfo = useCompanyInfo();
  const isInternal = useSelector(isInternalSelector);
  // "Export data" and the HROAS scenario notes are gated to the same product/econ/engineering/admin
  // user groups as FeatureExperiments edit access (EDITOR_USER_GROUPS) — both surface internal
  // diagnostics (the raw analyze lambda response, model plan notes), so they're scoped to the same
  // technical audience rather than a new department field.
  const userGroups = useUserGroups();
  const hasEditorGroupAccess = userGroups.some(g =>
    (EDITOR_USER_GROUPS as readonly string[]).includes(g)
  );
  // Stable (empty-deps) indirection into exportDataRef, kept up to date by the effect further down
  // (once baselineData/currentScenarioDataMap are in scope) — needed because this callback must be
  // passed into useScenarioPlanningToolBar below, before those values are computed, and a raw
  // closure over them here would defeat the React Compiler's memoization of the baselineData-derived
  // useMemos further down (it can't prove an escaping closure doesn't mutate them).
  const exportDataRef = useRef<() => void>(() => {});
  const handleExportData = useCallback(() => exportDataRef.current(), []);
  // The lambda returns every run tagged with clientFacing; non-client-facing runs are shown to
  // internal users only (via the Model Info picker). Everything downstream reads this filtered
  // list, so external users behave exactly as before (client-facing runs only).
  const visibleModels = useMemo(
    () => (scenarioPlanningModels ?? []).filter(model => isInternal || model.clientFacing),
    [scenarioPlanningModels, isInternal]
  );

  // The model for the page's selected run. `undefined` with a run set means that run has no
  // scenario model (e.g. a Brand Equity admin-branch run, or a legacy rMMM run without artifacts).
  const hasSharedRun = sharedRun !== undefined;
  const sharedModel = sharedRun?.model;
  const runUnavailable = hasSharedRun && usesDynamicModels(modelType) && sharedModel === undefined;

  const outcomeVariables = useMemo((): string[] | undefined => {
    if (modelType === MODEL_TYPES.GEO_MMM) {
      if (scenarioPlanningModels == null) {
        return undefined;
      }
      // With a page-selected run the KPI comes from that run, not from this dropdown.
      if (hasSharedRun) {
        return sharedModel ? [sharedModel.kpi] : undefined;
      }
      return Array.from(new Set(visibleModels.map(model => model.kpi)));
    }
    // rMMM: show the currently-selected run's real KPI name, kept single-valued since rMMM's
    // model selection isn't coupled to outcomeVariable (see selectedModel below).
    if (modelType === MODEL_TYPES.R_MMM) {
      if (scenarioPlanningModels == null) {
        return undefined;
      }
      const current =
        sharedModel ??
        visibleModels.find(model => modelRunKey(model) === selectedModelKey) ??
        pickDefaultModel(visibleModels);
      return [current ? current.kpi : 'revenue'];
    }
    return ['revenue'];
  }, [
    modelType,
    scenarioPlanningModels,
    visibleModels,
    selectedModelKey,
    hasSharedRun,
    sharedModel
  ]);

  const { end, groupBy, outcomeVariable, start, setGroupBy, setOutcomeVariable, toolbar } =
    useScenarioPlanningToolBar({
      adjustScenario: (option, newPlanSettings): void => {
        if (!newScenarioTree || !newPlanSettings) {
          return;
        }
        if (option === PLAN_OPTIONS.START_FRESH) {
          setNewScenarioTree(undefined);
          setCurrentScenarioDataMap(undefined);
          setOldScenarioSnapshot([]);
          setPairwiseData([]);
          setShowChangeImpactModal(false);
          setShowEditScenarioModal(false);
          setIsFirstScenarioRun(true);
          setLastAnalyzeRawResult(undefined);
          return;
        }
        // Use newPlanSettings (not closure-captured start/end, still pre-commit) so
        // the rebuilt baseline matches the chart once setRange settles.
        const newBaselineRows = sliceBaselineRows(
          rawBaselineData,
          newPlanSettings.start,
          newPlanSettings.end,
          isHroas
        );
        const newBaselineTree = scenarioRedshiftDataToTree(newBaselineRows);
        const adjustedScenario = adjustScenario({
          currentScenarioTree: newScenarioTree,
          newBaselineTree,
          option
        });
        setIsAnalyzing(true);
        setPageProgressPercent(null);
        (async () => {
          try {
            const {
              dataMap: newScenarioData,
              pairwiseData: newPairwiseData,
              rawResult
            } = await analyzeScenario({
              end: newPlanSettings.end,
              modelConfig: modelConfig!,
              newScenarioTree: adjustedScenario,
              onProgress: progress => setPageProgressPercent(progress.percent ?? null),
              start: newPlanSettings.start
            });
            setNewScenarioTree(updateTreeWithAnalyzedData(adjustedScenario, newScenarioData));
            // Anchor "Previous" to the new-range baseline tree so ChangeImpactModal
            // compares scenario-at-new-range vs baseline-at-new-range.
            setOldScenarioSnapshot(newBaselineTree);
            if (newPairwiseData) {
              setPairwiseData(newPairwiseData);
            }
            handleEditScenario(newScenarioData, undefined, rawResult);
          } catch (e) {
            setNewScenarioTree(adjustedScenario);
            const reportError = e as Error;
            setError({ message: reportError.message, reportError });
          } finally {
            setIsAnalyzing(false);
          }
        })();
      },
      hasScenario: Boolean(newScenarioTree),
      maxEnd,
      minStart,
      onExportData: hasEditorGroupAccess ? handleExportData : undefined,
      onReopenChangeImpact:
        newScenarioTree && currentScenarioDataMap
          ? () => setShowChangeImpactModal(true)
          : undefined,
      outcomeVariables,
      pinToPageChrome: true
    });

  // The page's selected run decides the model. Without one (nothing passed yet) fall back to the
  // default run (most-recent client-facing, then most recent). For GeoMMM the fallback is coupled
  // to the Outcome Variable dropdown (which drives KPI), so candidates are scoped to the current KPI.
  const selectedModel = useMemo(() => {
    if (hasSharedRun) {
      return sharedModel;
    }
    const candidates =
      modelType === MODEL_TYPES.GEO_MMM
        ? visibleModels.filter(m => m.kpi === outcomeVariable)
        : visibleModels;
    if (candidates.length === 0) {
      return undefined;
    }
    return (
      candidates.find(m => modelRunKey(m) === selectedModelKey) ?? pickDefaultModel(candidates)
    );
  }, [modelType, outcomeVariable, visibleModels, selectedModelKey, hasSharedRun, sharedModel]);

  // On first load (and whenever the visible models change without a valid selection), default to
  // the most-recent client-facing run, else the most-recent run overall, and sync the KPI
  // dropdown to it. External users with no client-facing runs fall through to the no-models error.
  useEffect(() => {
    // Only dynamic-model types have run selection, and a page-selected run overrides the default.
    if (!usesDynamicModels(modelType) || scenarioPlanningModels === undefined || hasSharedRun) {
      return;
    }
    const selectionIsValid = visibleModels.some(m => modelRunKey(m) === selectedModelKey);
    if (selectionIsValid) {
      return;
    }
    const defaultModel = pickDefaultModel(visibleModels);
    if (defaultModel) {
      setSelectedModelKey(modelRunKey(defaultModel));
      // GeoMMM couples model choice to the KPI dropdown; rMMM's outcome is revenue-only.
      if (modelType === MODEL_TYPES.GEO_MMM) {
        setOutcomeVariable(defaultModel.kpi);
      }
    }
  }, [
    modelType,
    scenarioPlanningModels,
    visibleModels,
    selectedModelKey,
    hasSharedRun,
    setOutcomeVariable
  ]);

  // Follow the page's selected run: select its model and sync the KPI dropdown to it.
  useEffect(() => {
    if (!sharedModel) {
      return;
    }
    setSelectedModelKey(modelRunKey(sharedModel));
    if (modelType === MODEL_TYPES.GEO_MMM || modelType === MODEL_TYPES.R_MMM) {
      setOutcomeVariable(sharedModel.kpi);
    }
  }, [modelType, sharedModel, setOutcomeVariable]);

  // HROAS model selection comes from the selected run (hroasRun); only the include/exclude and
  // weights are client-side. Still calls the real analyze/getBaseline HROAS lambdas.
  const hroasModelConfig: HROASModelConfig | undefined =
    modelType === MODEL_TYPES.HROAS && hroasRun && hroasModels.length > 0
      ? {
          modelType: MODEL_TYPES.HROAS,
          cid: hroasRun.cid,
          kpi: hroasRun.kpi,
          runId: hroasRun.runId,
          slotIds: hroasModels.map(m => m.id)
        }
      : undefined;

  const modelConfig =
    modelType === MODEL_TYPES.HROAS ? hroasModelConfig : getModelConfig(selectedModel);
  const modelCacheKey = getModelCacheKey(modelConfig);
  // Feature-map normalization (opaque id -> pretty name) happens in this pure helper so the React
  // Compiler memoizes it and every downstream consumer (chart, tree, both modals, filters) reacts
  // to the async featureMapping. Originals are preserved on the rows for the analyze payload.
  const rawBaselineData = normalizeBaselineData(
    rawBaselineDataMap[modelCacheKey],
    featureMapping,
    featureMaps
  );

  // Whether the model's outcome is denominated in dollars (revenue KPI -> ROAS) vs raw
  // conversion counts (non-revenue KPI -> CPX). Sourced from the model's actual kpi_type,
  // surfaced by the lambda as outcomeUnit/kpiType — NOT the KPI name.
  useEffect(() => {
    if (!rawBaselineData) {
      return;
    }
    const { outcomeUnit, kpiType, baselineRevenue } = rawBaselineData;
    if (outcomeUnit !== undefined || kpiType !== undefined) {
      setIsRevenueData(outcomeUnit === 'dollars' || kpiType === 'revenue');
      return;
    }
    // Fallback for older lambdas that don't yet emit outcomeUnit/kpiType: preserve the
    // previous heuristic (presence of directRevenue) so deploy ordering can't regress.
    const firstRow = baselineRevenue?.rows?.[0];
    setIsRevenueData(firstRow?.directRevenue !== undefined && firstRow?.directRevenue !== null);
  }, [rawBaselineData]);

  // Re-sync the selected chart metrics when the model's revenue-ness resolves (or on model
  // switch). Revenue models default to Spend + Revenue; non-revenue models to Spend + KPI
  // Volume — otherwise the picker stays stuck on Revenue (dollars) for a conversion model.
  useEffect(() => {
    if (isRevenueData === undefined) {
      return;
    }
    setChartVariables(
      isRevenueData
        ? [SCENARIO_METRIC_ENUM.SPEND, SCENARIO_METRIC_ENUM.REVENUE]
        : [SCENARIO_METRIC_ENUM.SPEND, SCENARIO_METRIC_ENUM.KPI_VOLUME]
    );
  }, [isRevenueData]);

  const chartVariableOptions = useMemo(() => {
    if (isRevenueData) {
      return [SCENARIO_METRIC_ENUM.SPEND, SCENARIO_METRIC_ENUM.REVENUE, SCENARIO_METRIC_ENUM.ROAS];
    }
    return [SCENARIO_METRIC_ENUM.SPEND, SCENARIO_METRIC_ENUM.KPI_VOLUME, SCENARIO_METRIC_ENUM.CPX];
  }, [isRevenueData]);

  // Display token for metric labels only. A revenue model's outcome is "Revenue"/"ROAS";
  // a non-revenue model's labels derive from its KPI name (e.g. "Orders" -> "Cost Per Order").
  // The raw outcomeVariable (the KPI name) is still used for model selection above.
  const labelOutcomeVariable = isRevenueData ? 'revenue' : outcomeVariable;

  // ==== HROAS: per-model date coverage + the included-models intersection (PRD §6.2). Plain
  // consts, not useMemo — same "React Compiler memoizes pure derived consts" convention as
  // rawBaselineData above. Depends only on rawBaselineData/isHroas/includedModelIds (NOT
  // baselineData), so it's placed before baselineData's own declaration below — keeping it there
  // avoids sitting between baselineData's definition and the useMemo hooks that consume it
  // further down, which breaks React Compiler's manual-memoization preservation for those hooks
  // even though this new code never touches baselineData itself. ====
  const hroasModelDateBounds = computeHroasModelDateBounds(isHroas, rawBaselineData);
  const hroasDateIntersection = computeHroasIncludedDateIntersection(
    isHroas,
    hroasModelDateBounds,
    includedModelIds
  );

  // Sync the toolbar's selectable date range from the baseline rows (independent of the current
  // start/end selection). Done in an effect — not render — because the toolbar is created above,
  // before the data resolves, and reads these bounds from state once the data loads.
  //
  // HROAS: bounds must be the INTERSECTION of only the currently-included models' coverage (see
  // computeHroasIncludedDateIntersection above), not the union of every model's rows scanned
  // together below — a naive union finds the widest possible range instead of the narrowest common
  // one. When included models have zero overlap, fall back to the union of just those included
  // models (never all configured models) so the toolbar always has a non-empty range to init from;
  // any resulting no-data pockets are already caught by the untouched computeHroasNoDataModelIds
  // warning above, so no second warning surface is added here.
  useEffect(() => {
    if (isHroas) {
      const boundsStart = hroasDateIntersection.hasOverlap
        ? hroasDateIntersection.intersectionStart
        : hroasDateIntersection.unionStart;
      const boundsEnd = hroasDateIntersection.hasOverlap
        ? hroasDateIntersection.intersectionEnd
        : hroasDateIntersection.unionEnd;
      // No included model has any coverage at all (e.g. none have loaded rows yet) — clear the
      // range instead of leaving the toolbar showing a stale previous selection.
      setMinStart(boundsStart);
      setMaxEnd(boundsEnd);
      return;
    }
    const spendRows = rawBaselineData?.baselineSpend?.rows ?? [];
    const revenueRows = rawBaselineData?.baselineRevenue?.rows ?? [];
    let minDate = '';
    let maxDate = '';
    // Perf: track running min/max as timestamps instead of re-parsing minDate/maxDate strings each iter.
    let minMs = Infinity;
    let maxMs = -Infinity;
    const len = Math.min(spendRows.length, revenueRows.length);
    for (let i = 0; i < len; i++) {
      if (!revenueRows[i]) {
        continue;
      }
      const { week } = spendRows[i];
      const rowMs = new Date(week).getTime();
      if (rowMs > maxMs) {
        maxMs = rowMs;
        maxDate = week;
      }
      if (rowMs < minMs) {
        minMs = rowMs;
        minDate = week;
      }
    }
    if (maxDate) {
      setMaxEnd(maxDate);
    }
    if (minDate) {
      setMinStart(minDate);
    }
  }, [isHroas, hroasDateIntersection, rawBaselineData]);

  const baselineData = computeBaselineData(rawBaselineData, start, end, isHroas);

  // Keeps exportDataRef (read by the stable handleExportData above) pointing at a closure over
  // the latest baselineData/currentScenarioDataMap/lastAnalyzeRawResult.
  useEffect(() => {
    exportDataRef.current = () => {
      if (baselineData.length === 0) {
        return;
      }
      exportScenarioPlanningData({
        baselineData,
        scenarioDataMap: currentScenarioDataMap,
        lastAnalyzeRawResult,
        fileNamePrefix: `${companyInfo.cid}_${modelType}`
      });
      Mixpanel.track(MxE.ActionTriggered, { page: 'ScenarioPlanning', action: 'export_data' });
    };
  }, [baselineData, currentScenarioDataMap, lastAnalyzeRawResult, companyInfo.cid, modelType]);

  const aggregatedBaselineData = useMemo(() => {
    const aggregationMap: Record<string, ScenarioDateRow & { flightingData: ScenarioDateRow[] }> =
      {};
    for (const row of baselineData) {
      const { directSpend, directRevenue } = row;
      if (!hasValidDirects(directSpend, directRevenue)) {
        continue;
      }
      // ==== HROAS: revenue-only weighting/exclusion — spend is never touched (PRD §5.1). ====
      const revWeight = getModelRevenueWeight(
        row,
        isHroas ? includedModelIds : undefined,
        isHroas ? normalizedModelWeights : undefined
      );
      const weightedDirectRevenue = directRevenue * revWeight;
      const weightedIndirectRevenue =
        row.indirectRevenue !== undefined ? row.indirectRevenue * revWeight : undefined;
      const weightedReattributedOutRevenue =
        row.reattributedOutRevenue !== undefined
          ? row.reattributedOutRevenue * revWeight
          : undefined;
      const itemKey = getGroupKey(row, groupBy);
      const existing = aggregationMap[itemKey];
      if (!existing) {
        aggregationMap[itemKey] = {
          ...row,
          directRevenue: weightedDirectRevenue,
          indirectRevenue: weightedIndirectRevenue,
          reattributedOutRevenue: weightedReattributedOutRevenue,
          // Unweighted per-model revenue for the segment stacks (weights are applied at read).
          byModel: isHroas ? mergeRowIntoModelRevenue(undefined, row) : undefined,
          flightingData: [row]
        };
      } else {
        existing.directSpend += directSpend;
        existing.directRevenue += weightedDirectRevenue;
        existing.indirectSpend = (existing.indirectSpend ?? 0) + (row.indirectSpend ?? 0);
        existing.indirectRevenue = (existing.indirectRevenue ?? 0) + (weightedIndirectRevenue ?? 0);
        existing.reattributedOutSpend =
          (existing.reattributedOutSpend ?? 0) + (row.reattributedOutSpend ?? 0);
        existing.reattributedOutRevenue =
          (existing.reattributedOutRevenue ?? 0) + (weightedReattributedOutRevenue ?? 0);
        if (isHroas) {
          existing.byModel = mergeRowIntoModelRevenue(existing.byModel, row);
        }
        existing.flightingData.push(row);
      }
    }
    const values = Object.values(aggregationMap);
    if (isSpDebug()) {
      console.warn('[rmmm-debug] aggregatedBaselineData buckets', {
        groupBy,
        buckets: values.map(v => ({
          itemKey: getGroupKey(v, groupBy),
          directSpend: v.directSpend,
          directRevenue: v.directRevenue,
          flightingRowCount: v.flightingData.length,
          isDemandCapture: v.isDemandCapture
        }))
      });
    }
    return values;
  }, [baselineData, groupBy, isHroas, includedModelIds, normalizedModelWeights]);

  const filteredAggregatedBaselineData = useMemo(() => {
    if (!filter) {
      return aggregatedBaselineData;
    }
    return aggregatedBaselineData.filter(filter.filter);
  }, [aggregatedBaselineData, filter]);

  const channelColorMap = useMemo(
    () =>
      buildChannelColorMap(
        aggregatedBaselineData.map(row => ({ id: getGroupKey(row, groupBy) })),
        companyInfo.cid === 'tinuititest'
      ),
    // Perf: depend on cid, not the whole companyInfo object.
    [aggregatedBaselineData, companyInfo.cid, groupBy]
  );

  const tacticDcLookup = useMemo(() => {
    const map = new Map<string, boolean>();
    const writesByKey: Record<string, boolean[]> = {};
    for (const row of baselineData) {
      const key = [
        capitalizeNames(row.channel),
        capitalizeNames(row.platform),
        capitalizeNames(row.tactic)
      ]
        .filter(v => v && !isNA(v))
        .join(', ');
      if (row.isDemandCapture !== undefined) {
        map.set(key, row.isDemandCapture);
        if (isSpDebug()) {
          (writesByKey[key] = writesByKey[key] || []).push(row.isDemandCapture);
        }
      }
    }
    if (isSpDebug()) {
      const conflicts = Object.entries(writesByKey)
        .map(([key, values]) => ({
          key,
          writtenValues: values,
          hasConflict: new Set(values).size > 1
        }))
        .filter(c => c.hasConflict);

      console.warn('[rmmm-debug] tacticDcLookup conflicts', { conflicts });
    }
    return map;
  }, [baselineData]);

  const currentAggregatedScenarioMap = useMemo(() => {
    if (!currentScenarioDataMap) {
      return {};
    }
    const acc: Record<string, ScenarioDateRow> = {};
    const debugByItemKey: Record<
      string,
      {
        rawKey: string;
        directSpend: number;
        parsed: { channel: string; platform: string; tactic: string };
        tacticKey: string;
        dcStatus: boolean | undefined;
      }[]
    > = {};
    for (const key of Object.keys(currentScenarioDataMap)) {
      const [channelRaw = '', platformRaw = '', tacticRaw = ''] = key.split(', ');
      const rowData = currentScenarioDataMap[key];
      // The scenario dataMap is keyed by the original feature id (analyze echoes the payload keys).
      // Resolve it to the same pretty channel/platform/tactic the baseline bars use so the scenario
      // overlay keys/labels line up with the (prettified) baseline bars. HROAS blends rMMM +
      // BrandEquity rows, so resolve against rowData's OWN model map — see normalizeBaselineData.
      const resolved = resolveFeatureParts(
        key,
        { channel: channelRaw, platform: platformRaw, tactic: tacticRaw },
        featureMaps?.[rowData?.modelType ?? ''] ?? featureMapping
      );
      const channel = capitalizeNames(resolved?.channel ?? channelRaw);
      const platform = capitalizeNames(resolved?.platform ?? platformRaw);
      const tactic = capitalizeNames(resolved?.tactic ?? tacticRaw);
      const tacticKey = [channel, platform, tactic].filter(v => v && !isNA(v)).join(', ');
      const dcStatus = tacticDcLookup.get(tacticKey);
      const itemKey = buildScenarioKey(channel, platform, tactic, groupBy, dcStatus);
      if (isSpDebug()) {
        (debugByItemKey[itemKey] = debugByItemKey[itemKey] || []).push({
          rawKey: key,
          directSpend: rowData?.directSpend ?? 0,
          parsed: { channel, platform, tactic },
          tacticKey,
          dcStatus
        });
      }
      const {
        directRevenueCiLo = 0,
        directSpend,
        directRevenue,
        directRevenueCiHi = 0,
        indirectSpend = 0,
        indirectRevenue = 0,
        indirectRevenueCiLo = 0,
        indirectRevenueCiHi = 0,
        reattributedOutSpend = 0,
        reattributedOutRevenue = 0,
        actualizedRevenueCiLo = 0,
        actualizedRevenueCiHi = 0
      } = rowData;
      if (!hasValidDirects(directSpend, directRevenue)) {
        continue;
      }
      // ==== HROAS: revenue-only weighting/exclusion — spend is never touched (PRD §5.1). CI
      // bounds are scaled by the same weight for consistency with the weighted mean; combining
      // CI across models is a data-science detail out of scope for this pass (PRD §5.7). ====
      const revWeight = getModelRevenueWeight(
        rowData,
        isHroas ? includedModelIds : undefined,
        isHroas ? normalizedModelWeights : undefined
      );
      // A blended row (2+ model instances sharing this feature) has no single modelId to weight
      // by, so getModelRevenueWeight returns 0 for it — correct for the CI bounds below (no
      // per-model breakdown to weight them by, PRD §5.7 scopes that out), but direct/indirect
      // revenue DO have a per-model breakdown (byModel) and must be weighted from that directly,
      // or the blended feature's revenue would collapse to 0 instead of its true weighted value.
      const isBlended = isHroas && !rowData.modelId && Boolean(rowData.byModel);
      const wDirectRevenue = isBlended
        ? sumWeightedByModel(
            rowData.byModel,
            includedModelIds,
            normalizedModelWeights,
            'directRevenue'
          )
        : directRevenue * revWeight;
      const wIndirectRevenue = isBlended
        ? sumWeightedByModel(
            rowData.byModel,
            includedModelIds,
            normalizedModelWeights,
            'indirectRevenue'
          )
        : indirectRevenue * revWeight;
      const wReattributedOutRevenue = isBlended
        ? sumWeightedByModel(
            rowData.byModel,
            includedModelIds,
            normalizedModelWeights,
            'reattributedOutRevenue'
          )
        : reattributedOutRevenue * revWeight;
      const wDirectRevenueCiLo = directRevenueCiLo * revWeight;
      const wDirectRevenueCiHi = directRevenueCiHi * revWeight;
      const wIndirectRevenueCiLo = indirectRevenueCiLo * revWeight;
      const wIndirectRevenueCiHi = indirectRevenueCiHi * revWeight;
      const wActualizedRevenueCiLo = actualizedRevenueCiLo * revWeight;
      const wActualizedRevenueCiHi = actualizedRevenueCiHi * revWeight;
      const existing = acc[itemKey];
      if (!existing) {
        acc[itemKey] = {
          // This is a DISPLAY-only aggregation bucket (merges every row sharing an itemKey), not
          // fed back into scenario_spend construction (see scenarioRedshiftDataToTree for that) —
          // a representative feature id is enough to satisfy the type.
          feature: rowData.feature,
          [ScenarioGranularityEnum.CHANNEL]: channel,
          [ScenarioGranularityEnum.PLATFORM]: platform,
          [ScenarioGranularityEnum.TACTIC]: tactic,
          // ==== HROAS (unmigrated): carry model instance + kind onto the aggregated scenario row ====
          modelId: rowData.modelId,
          modelLabel: rowData.modelLabel,
          modelType: rowData.modelType,
          date: '',
          directRevenueCiLo: wDirectRevenueCiLo,
          directRevenue: wDirectRevenue,
          directSpend,
          directRevenueCiHi: wDirectRevenueCiHi,
          indirectSpend,
          indirectRevenue: wIndirectRevenue,
          indirectRevenueCiLo: wIndirectRevenueCiLo,
          indirectRevenueCiHi: wIndirectRevenueCiHi,
          reattributedOutSpend,
          reattributedOutRevenue: wReattributedOutRevenue,
          actualizedRevenueCiLo: wActualizedRevenueCiLo,
          actualizedRevenueCiHi: wActualizedRevenueCiHi,
          byModel: isHroas ? mergeRowIntoModelRevenue(undefined, rowData) : undefined,
          isDemandCapture: dcStatus
        };
      } else {
        existing.directRevenueCiLo = (existing.directRevenueCiLo ?? 0) + wDirectRevenueCiLo;
        existing.directRevenue += wDirectRevenue;
        existing.directSpend += directSpend;
        existing.directRevenueCiHi = (existing.directRevenueCiHi ?? 0) + wDirectRevenueCiHi;
        existing.indirectSpend = (existing.indirectSpend ?? 0) + indirectSpend;
        existing.indirectRevenue = (existing.indirectRevenue ?? 0) + wIndirectRevenue;
        existing.indirectRevenueCiLo = (existing.indirectRevenueCiLo ?? 0) + wIndirectRevenueCiLo;
        existing.indirectRevenueCiHi = (existing.indirectRevenueCiHi ?? 0) + wIndirectRevenueCiHi;
        existing.reattributedOutSpend = (existing.reattributedOutSpend ?? 0) + reattributedOutSpend;
        existing.reattributedOutRevenue =
          (existing.reattributedOutRevenue ?? 0) + wReattributedOutRevenue;
        existing.actualizedRevenueCiLo =
          (existing.actualizedRevenueCiLo ?? 0) + wActualizedRevenueCiLo;
        existing.actualizedRevenueCiHi =
          (existing.actualizedRevenueCiHi ?? 0) + wActualizedRevenueCiHi;
        if (isHroas) {
          existing.byModel = mergeRowIntoModelRevenue(existing.byModel, rowData);
        }
      }
    }
    if (isSpDebug()) {
      const collisions = Object.entries(debugByItemKey)
        .filter(([, entries]) => entries.length > 1)
        .map(([itemKey, entries]) => ({
          itemKey,
          entries,
          totalSummedDirectSpend: entries.reduce((s, e) => s + e.directSpend, 0)
        }));

      console.warn('[rmmm-debug] currentAggregatedScenarioMap collisions', {
        groupBy,
        collisions,
        totalItemKeys: Object.keys(debugByItemKey).length,
        totalSourceRows: Object.keys(currentScenarioDataMap).length
      });
    }
    return acc;
  }, [
    currentScenarioDataMap,
    featureMapping,
    featureMaps,
    groupBy,
    tacticDcLookup,
    isHroas,
    includedModelIds,
    normalizedModelWeights
  ]);

  const sparkChartCards: SparkChartCardProps[] = useMemo(() => {
    // Sum a metric across rows the same way the per-row chart data already does (see the
    // getActualizedMetricValue calls below) instead of hand-writing the direct+indirect-
    // reattributedOut netting again here.
    const sumMetric = (rows: ScenarioDateRow[], metric: SCENARIO_METRIC_ENUM) =>
      rows.reduce(
        (s, r) =>
          s + (showActualized ? getActualizedMetricValue(r, metric) : getMetricValue(r, metric)),
        0
      );
    const scenarioEntries = Object.values(currentAggregatedScenarioMap);
    const filteredScenarioEntries = filter
      ? scenarioEntries.filter(filter.filter)
      : scenarioEntries;

    const bSpend = sumMetric(filteredAggregatedBaselineData, SCENARIO_METRIC_ENUM.SPEND);
    const sSpend =
      scenarioEntries.length > 0
        ? sumMetric(filteredScenarioEntries, SCENARIO_METRIC_ENUM.SPEND)
        : bSpend;
    const bRev = sumMetric(filteredAggregatedBaselineData, SCENARIO_METRIC_ENUM.REVENUE);
    const sRev =
      scenarioEntries.length > 0
        ? sumMetric(filteredScenarioEntries, SCENARIO_METRIC_ENUM.REVENUE)
        : bRev;

    // Revenue model: ROAS = revenue / spend (a ratio). Non-revenue model: cost per
    // conversion = spend / outcome (dollars).
    const costMetric = (rev: number, spend: number): number => {
      if (isRevenueData) {
        return spend > 0 ? rev / spend : 0;
      }
      return rev > 0 ? spend / rev : 0;
    };

    return [
      { title: 'Spend', baselineValue: bSpend, scenarioValue: sSpend, prefix: '$', decimals: 0 },
      {
        title: getEffectLabel(labelOutcomeVariable),
        baselineValue: bRev,
        scenarioValue: sRev,
        // Dollars for a revenue KPI; raw conversion counts otherwise.
        prefix: isRevenueData ? '$' : '',
        decimals: 0
      },
      {
        title: getCostMetricLabel(labelOutcomeVariable),
        baselineValue: costMetric(bRev, bSpend),
        scenarioValue: costMetric(sRev, sSpend),
        prefix: isRevenueData ? '' : '$',
        decimals: 2
      }
    ];
  }, [
    filteredAggregatedBaselineData,
    currentAggregatedScenarioMap,
    filter,
    labelOutcomeVariable,
    isRevenueData,
    showActualized
  ]);

  const flightingMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    if (newScenarioTree !== undefined && newScenarioTree !== null) {
      const traverse = (currentNodes: ScenarioNode[], prefix = '') => {
        for (const node of currentNodes) {
          const formattedName = capitalizeNames(stripDcDgMarker(node.name));
          const dcSuffix = dcSuffixFor(node);
          const key = keyFor(prefix, formattedName);
          map[`${key}${dcSuffix}`] = node.useHistoricalFlighting;
          if (node.children && node.children.length > 0) {
            traverse(node.children, key);
          }
        }
      };
      traverse(newScenarioTree);
    }
    return map;
  }, [newScenarioTree]);

  // "% of total" (Percentage view) denominator: the true total across every channel for a given
  // metric, computed from the full unfiltered aggregatedBaselineData — not filteredAggregatedBaselineData
  // — so a channel search filter never changes what a bar's percentage means (searching down to one
  // channel would otherwise always show it as 100%). ROAS/CPX are ratios, not summable, and never enter
  // percentage view (gated below alongside showPercentageView), so only these three need a total.
  const metricGrandTotals = useMemo(() => {
    const totals: Partial<Record<SCENARIO_METRIC_ENUM, number>> = {};
    for (const metric of [
      SCENARIO_METRIC_ENUM.SPEND,
      SCENARIO_METRIC_ENUM.REVENUE,
      SCENARIO_METRIC_ENUM.KPI_VOLUME
    ]) {
      totals[metric] = aggregatedBaselineData.reduce((sum, baselineRow) => {
        const scenarioKeyBase = getScenarioKeyBase(baselineRow, groupBy);
        const sourceRow = currentAggregatedScenarioMap[scenarioKeyBase] ?? baselineRow;
        const directValue = getMetricValue(sourceRow, metric);
        const actualizedValue = getActualizedMetricValue(sourceRow, metric);
        return sum + (showActualized ? actualizedValue : directValue);
      }, 0);
    }
    return totals;
  }, [aggregatedBaselineData, currentAggregatedScenarioMap, groupBy, showActualized]);

  const barChartData = useMemo(() => {
    // Perf: compute once instead of allocating Object.keys() on every row × metric.
    const hasScenario = Object.keys(currentAggregatedScenarioMap).length > 0;
    return chartVariables.map(metric =>
      filteredAggregatedBaselineData.map(baselineRow => {
        const id = getGroupKey(baselineRow, groupBy);
        const label = getChartLabel(baselineRow, groupBy);
        const scenarioKeyBase = getScenarioKeyBase(baselineRow, groupBy);
        const baselineDirect = getMetricValue(baselineRow, metric);
        const baselineActualized = getActualizedMetricValue(baselineRow, metric);
        const baselineValue = showActualized ? baselineActualized : baselineDirect;
        const scenarioRow = currentAggregatedScenarioMap[scenarioKeyBase];
        const sourceRow = scenarioRow ?? baselineRow;
        const directValue = getMetricValue(sourceRow, metric);
        const sourceActualized = getActualizedMetricValue(sourceRow, metric);
        const indirectValue = sourceActualized - directValue;
        let confidenceInterval: [number, number] | undefined;
        if (
          scenarioRow?.directRevenueCiLo !== undefined &&
          scenarioRow?.directRevenueCiLo !== null &&
          scenarioRow?.directRevenueCiHi !== undefined &&
          scenarioRow?.directRevenueCiHi !== null &&
          metric !== SCENARIO_METRIC_ENUM.SPEND
        ) {
          let lower: number;
          let upper: number;
          if (metric === SCENARIO_METRIC_ENUM.ROAS) {
            // ROAS = outcome / spend: bounds scale directly with the outcome CI.
            lower = scenarioRow.directRevenueCiLo / scenarioRow.directSpend;
            upper = scenarioRow.directRevenueCiHi / scenarioRow.directSpend;
          } else if (metric === SCENARIO_METRIC_ENUM.CPX) {
            // CPX = spend / outcome: inverse, so the bounds flip (higher outcome -> lower cost).
            lower = scenarioRow.directRevenueCiHi
              ? scenarioRow.directSpend / scenarioRow.directRevenueCiHi
              : 0;
            upper = scenarioRow.directRevenueCiLo
              ? scenarioRow.directSpend / scenarioRow.directRevenueCiLo
              : 0;
          } else {
            // REVENUE / KPI_VOLUME: the outcome CI as-is.
            lower = scenarioRow.directRevenueCiLo;
            upper = scenarioRow.directRevenueCiHi;
          }
          confidenceInterval = [lower, upper];
        }
        // ==== HROAS: drive the direct/indirect breakout PER-ROW off the bucket's own
        // (correctly-summed) indirect values, so geoMMM bars stay solid while rMMM and
        // Brand Equity bars (reclassifyBrandEquityRevenueAsIndirect) both show a breakout in a
        // mixed holistic view. Previously gated on isRmmmRow(baselineRow.modelType), which is a
        // bucket-level field that's only accurate for Model grouping (see aggregatedBaselineData)
        // and predates Brand Equity's revenue being reclassified as indirect — that gate was
        // incorrectly forcing Brand Equity bars solid and would misfire on any blended bucket. ====
        const rowSupportsIndirect =
          sourceRow.indirectRevenue != null || sourceRow.indirectSpend != null;
        // ==== HROAS: a bucket can blend rows from several models and keeps the first row's
        // modelId, so derive the tooltip's "source model(s)" from each raw contributing row in
        // flightingData. modelLabel falls back to the run slot's label. ====
        const contributingModels = Array.from(
          (baselineRow.flightingData ?? [])
            .reduce((byModelId, r) => {
              if (r.modelId && !byModelId.has(r.modelId)) {
                const modelLabel =
                  r.modelLabel ?? hroasModels.find(m => m.id === r.modelId)?.label ?? r.modelId;
                byModelId.set(r.modelId, { id: r.modelId, label: modelLabel });
              }
              return byModelId;
            }, new Map<string, { id: string; label: string }>())
            .values()
        );
        const segments =
          segmentsActive &&
          (metric === SCENARIO_METRIC_ENUM.REVENUE || metric === SCENARIO_METRIC_ENUM.KPI_VOLUME)
            ? getModelSegments(
                sourceRow.byModel,
                hroasModels,
                includedModelIds,
                normalizedModelWeights
              )
            : undefined;
        return {
          baselineValue,
          confidenceInterval,
          directValue,
          segments,
          hasScenario,
          id,
          indirectValue,
          showActualized,
          showBreakout: showActualized && rowSupportsIndirect && showCrossChannelInteractions,
          showPercentageView:
            showPercentageView &&
            (metric === SCENARIO_METRIC_ENUM.SPEND ||
              metric === SCENARIO_METRIC_ENUM.REVENUE ||
              metric === SCENARIO_METRIC_ENUM.KPI_VOLUME),
          isDemandCapture: baselineRow.isDemandCapture,
          label,
          // ==== HROAS: contributingModels lets the tooltip show every model that fed this bucket.
          // Bars always color by channel. ====
          contributingModels,
          flightingData: baselineRow.flightingData,
          metric,
          // ==== HROAS (unmigrated): stamp the REAL per-row model kind (mapped to the enum) so the
          // tooltip's Direct/Actualized prefix + any per-bar logic are correct in a mixed view.
          // Single-model pages keep the page modelType. ====
          modelType:
            modelType === MODEL_TYPES.HROAS
              ? (ROW_MODEL_TYPE_TO_ENUM[baselineRow.modelType ?? ''] ?? modelType)
              : (modelType as MODEL_TYPES),
          useHistoricalFlighting:
            flightingMap[scenarioKeyBase] ||
            newScenarioTree === undefined ||
            newScenarioTree === null,
          totalValue: metricGrandTotals[metric] ?? 0
        };
      })
    );
  }, [
    chartVariables,
    currentAggregatedScenarioMap,
    filteredAggregatedBaselineData,
    flightingMap,
    groupBy,
    hroasModels,
    metricGrandTotals,
    modelType,
    newScenarioTree,
    showActualized,
    showCrossChannelInteractions,
    showPercentageView,
    segmentsActive,
    includedModelIds,
    normalizedModelWeights
  ]);

  const sortedBarChartKeys = useMemo(() => {
    const dir = sortDirection === SORT_DIRECTION_ENUM.HIGH_TO_LOW ? -1 : 1;
    const resolve = (row: ScenarioDateRow): ScenarioDateRow => {
      const scenarioKey = buildScenarioKey(
        capitalizeNames(row.channel),
        capitalizeNames(row.platform),
        capitalizeNames(row.tactic),
        groupBy,
        row.isDemandCapture
      );
      return currentAggregatedScenarioMap[scenarioKey] ?? row;
    };
    const isAlpha = sortBy === CHART_SORT_BY_ENUM.ALPHABETICALLY;
    // Perf: decorate-sort-undecorate — derive each row's sort key ONCE (n times) instead of
    // recomputing capitalizeNames/buildScenarioKey/localeCompare inside the O(n log n) comparator.
    const sortValue = (row: ScenarioDateRow): number => {
      const r = resolve(row);
      if (sortBy === CHART_SORT_BY_ENUM.SPEND) return r.directSpend;
      if (
        sortBy === CHART_SORT_BY_ENUM.REVENUE ||
        sortBy === CHART_SORT_BY_ENUM.REVENUE_WATERFALL
      ) {
        return r.directRevenue;
      }
      if (sortBy === CHART_SORT_BY_ENUM.ROAS)
        return r.directSpend ? r.directRevenue / r.directSpend : 0;
      return 0;
    };
    const decorated = filteredAggregatedBaselineData.map(row => ({
      row,
      label: isAlpha ? getChartLabel(row, groupBy) : '',
      value: isAlpha ? 0 : sortValue(row)
    }));
    decorated.sort((a, b) =>
      isAlpha ? dir * a.label.localeCompare(b.label) : dir * (a.value - b.value)
    );
    return decorated.map(d => d.row);
  }, [
    currentAggregatedScenarioMap,
    filteredAggregatedBaselineData,
    groupBy,
    sortBy,
    sortDirection
  ]);

  const sortedBarChartData = useMemo(() => {
    const lookupMaps = barChartData.map(chart => new Map(chart.map(item => [item.id, item])));
    const sortedKeys = sortedBarChartKeys.map(row => getGroupKey(row, groupBy));
    return lookupMaps.map(lookup =>
      sortedKeys.reduce<ScenarioPlanningChartData[]>((acc, key) => {
        const item = lookup.get(key);
        if (item) {
          acc.push(item);
        }
        return acc;
      }, [])
    );
  }, [barChartData, groupBy, sortedBarChartKeys]);

  // ==== HROAS: an included model can have zero rows in the selected date window (e.g. its last
  // observation predates the window start) and otherwise silently vanish from the chart with no
  // indication why. Detect that here and fold it into the same warning list as backend load
  // failures, so there's one unified dialog rather than two. Plain computation (not useMemo)
  // matching computeBaselineData above — rawBaselineData/baselineData already aren't manually
  // memoized in this component, so the React Compiler handles it. ====
  const hroasNoDataModelIds = computeHroasNoDataModelIds(
    isHroas,
    rawBaselineData,
    baselineData,
    includedModelIds,
    hroasModels
  );

  const unmatchedRevenueRowCount = isHroas
    ? countRevenueRowsWithoutSpendSource(rawBaselineData)
    : 0;

  const hroasAnalyzeNotices = filterHroasNotesForViewer(
    buildHroasAnalyzeNotices(isHroas ? lastAnalyzeRawResult : undefined),
    hasEditorGroupAccess
  );
  const hroasAllWarnings: HroasDisplayWarning[] = [
    // Run slots left out up front: the model exists in the run but lacks Scenario Planning artifacts.
    ...(hroasRun?.skippedSlots ?? []).map(slot => ({
      modelId: slot.slotId,
      label: slot.label,
      message: 'is not ready for Scenario Planning, so it is not included.',
      detail: `Missing: ${(slot.spMissingArtifacts ?? []).join(', ')}`
    })),
    ...(rawBaselineData?.warnings ?? []).map(w => ({
      modelId: w.modelId,
      message: 'failed to load — showing partial results.',
      detail: w.error
    })),
    // ==== PRD §6.2: hroasModelDateBounds (computed above for the toolbar's date-range
    // intersection) already knows each model's actual min/max coverage — surface it here instead
    // of a bare "no data" message with no indication of what range the model does cover. ====
    // Revenue rows whose feature has no spend-source counterpart are kept with zero spend.
    ...(unmatchedRevenueRowCount > 0
      ? [
          {
            modelId: 'hroas-unmatched-revenue',
            label: 'Revenue',
            message: `${unmatchedRevenueRowCount} revenue rows have no matching spend feature; they are kept with $0 spend.`
          }
        ]
      : []),
    // Backend analyze results: failed/partial models and plan-translation notes.
    ...hroasAnalyzeNotices,
    ...hroasNoDataModelIds.map(modelId => {
      const bounds = hroasModelDateBounds[modelId];
      return {
        modelId,
        message: 'has no data for the selected date range.',
        detail: bounds ? `Available data: ${bounds.minDate} to ${bounds.maxDate}.` : undefined
      };
    })
  ];

  // Informational plan notes (the model ran, possibly self-corrected) are shown apart from the
  // failures, and only failures open the dialog on their own.
  const hroasFailureWarnings = hroasAllWarnings.filter(w => w.kind !== 'note');
  const hroasNoteWarnings = hroasAllWarnings.filter(w => w.kind === 'note');

  // hroasFailureWarnings is a freshly-built array every render (not memoized, see above), so the
  // hook takes its length: it only reacts when failures actually appear or clear.
  const {
    show: showHroasWarningModal,
    open: openHroasWarningModal,
    close: closeHroasWarningModal
  } = useHroasWarningDialog(isHroas, hroasFailureWarnings.length);

  const renderHroasWarningRow = (w: HroasDisplayWarning) => (
    <div key={`${w.modelId}-${w.message}`} className='sp-hroas-warning-row'>
      <div>
        <strong>{w.label ?? hroasModels.find(m => m.id === w.modelId)?.label ?? w.modelId}</strong>{' '}
        {w.message}
      </div>
      {/* ==== PRD §6.2: surface the per-model available date range (or the raw
          load error) as visible text, not just an on-hover title attribute — it
          was previously invisible without hovering. ==== */}
      {w.detail && <div className='sp-hroas-warning-detail'>{w.detail}</div>}
    </div>
  );

  // Cap the chart container at the width its bar chart needs when bars hit
  // MAX_BAR_WIDTH, so extra horizontal space becomes centered grey background
  // instead of unused chart whitespace. The +48 accounts for the wrapper's
  // 24px side padding (or matching 24px scroll-icon columns when scrolling).
  const chartContainerMaxWidth = useMemo(() => {
    const maxBars = sortedBarChartData.reduce((max, chart) => Math.max(max, chart.length), 0);
    if (maxBars === 0) {
      return undefined;
    }
    const chartWidth =
      Y_AXIS_WIDTH + maxBars * MAX_BAR_WIDTH + Math.max(0, maxBars - 1) * MIN_BAR_GAP;
    // HROAS-only: the model-selection-bar/legend-bar need room scaling with the model count,
    // which MIN_CHART_CONTAINER_WIDTH alone doesn't account for. 0 for every other model type,
    // so their sizing is byte-for-byte unchanged.
    const hroasModelBarWidth = isHroas
      ? HROAS_BAR_CHROME_WIDTH + hroasModels.length * HROAS_MODEL_CHIP_WIDTH_ESTIMATE
      : 0;
    // rMMM/HROAS only: the Actualized/Absolute dropdowns need extra header room the base floor
    // doesn't cover (see RMMM_CONTROLS_EXTRA_WIDTH). 0 for GeoMMM/BrandEquity.
    const headerControlsWidth =
      (isHroas ? HROAS_MIN_CHART_CONTAINER_WIDTH : MIN_CHART_CONTAINER_WIDTH) +
      (showRmmmControls ? RMMM_CONTROLS_EXTRA_WIDTH : 0);
    // Don't let the few-bars shrink go so far that the header controls wrap/overlap.
    return Math.max(chartWidth + 48, headerControlsWidth, hroasModelBarWidth);
  }, [sortedBarChartData, isHroas, hroasModels.length, showRmmmControls]);

  const updateScrollState = useCallback(() => {
    const el = chartsColumnRef.current;
    if (!el) {
      return;
    }
    setScrollAtStart(el.scrollLeft <= 0);
    setScrollAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    setCanScrollCharts(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    const el = chartsColumnRef.current;
    if (!el) {
      return undefined;
    }
    const observer = new ResizeObserver(entries => {
      setChartsColumnWidth(entries[0].contentRect.width);
      updateScrollState();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState]);

  useEffect(() => {
    requestAnimationFrame(updateScrollState);
  }, [sortedBarChartData, updateScrollState]);

  useEffect(() => {
    if (!modelConfig || rawBaselineData || inFlightModelCacheKeyRef.current === modelCacheKey) {
      return;
    }
    inFlightModelCacheKeyRef.current = modelCacheKey;
    const fetchData = async () => {
      setPageProgressPercent(null);
      const onProgress = (progress: CometProgress) =>
        setPageProgressPercent(progress.percent ?? null);
      try {
        let data: BaselineData;
        if (modelConfig.modelType === MODEL_TYPES.GEO_MMM) {
          data = await getGeoMMMBaselineScenario(modelConfig.modelS3Key, onProgress);
        } else if (modelConfig.modelType === MODEL_TYPES.BRAND_EQUITY) {
          data = await getBrandEquityBaselineScenario(
            {
              stageOneModels: modelConfig.stageOneModels,
              stageTwoModelS3Key: modelConfig.stageTwoModelS3Key
            },
            onProgress
          );
        } else if (modelConfig.modelType === MODEL_TYPES.HROAS) {
          data = await getHROASBaselineScenarioLambda(toHroasRunArgs(modelConfig), onProgress);
        } else {
          data = await getRMMMBaselineScenario(
            {
              fxzModelS3Key: modelConfig.fxzModelS3Key,
              configS3Key: modelConfig.configS3Key,
              clientDataS3Key: modelConfig.clientDataS3Key,
              gxWorkspaceS3Key: modelConfig.gxWorkspaceS3Key,
              gxTracesS3Keys: modelConfig.gxTracesS3Keys
            },
            onProgress
          );
        }
        setRawBaselineDataMap(prev => ({ ...prev, [modelCacheKey]: data }));
      } catch (e) {
        const reportError = e as Error;
        setRawBaselineDataMap(prev => ({
          ...prev,
          [modelCacheKey]: {
            baselineSpend: { columns: [], rows: [], totalCount: 0 },
            baselineRevenue: { columns: [], rows: [], totalCount: 0 }
          }
        }));
        setError({ message: reportError.message, reportError });
      }
    };
    fetchData();
  }, [modelConfig, modelCacheKey, rawBaselineData, setError]);

  // The models come from the page's run rows; SP no longer fetches its own list. HROAS has no
  // single-select run list — it uses the hardcoded hroasModelConfig above.
  useEffect(() => {
    if (modelType === MODEL_TYPES.HROAS) {
      return;
    }
    setScenarioPlanningModels(runModels ?? []);
  }, [modelType, runModels]);

  // Dynamic-model SP (GeoMMM, rMMM) with no visible models has nothing to load, so the baseline
  // fetch never runs and the loading dialog would otherwise spin forever. HROAS has no dynamic
  // fetch (see hroasModelConfig above) — its "nothing to load" case is simply an unsupported
  // company, i.e. hroasModelConfig staying undefined.
  const hasNoModels =
    modelType === MODEL_TYPES.HROAS
      ? hroasModelConfig === undefined
      : usesDynamicModels(modelType) &&
        Array.isArray(scenarioPlanningModels) &&
        visibleModels.filter(model => !isLegacyRmmmRun(model)).length === 0;

  const handleChartScrollLeft = useCallback(() => {
    chartsColumnRef.current?.scrollBy({ left: -CHART_SCROLL_AMOUNT, behavior: 'smooth' });
  }, []);

  const handleChartScrollRight = useCallback(() => {
    chartsColumnRef.current?.scrollBy({ left: CHART_SCROLL_AMOUNT, behavior: 'smooth' });
  }, []);

  const handleChartsScroll = useCallback(() => {
    updateScrollState();
  }, [updateScrollState]);

  const handleEditScenario = useCallback(
    (
      dataMap: Record<string, ScenarioDateRow>,
      newPairwiseData?: PairwiseInteraction[],
      rawResult?: AnalyzeScenarioResult
    ) => {
      setCurrentScenarioDataMap(dataMap);
      if (newPairwiseData) {
        setPairwiseData(newPairwiseData);
      }
      if (rawResult) {
        setLastAnalyzeRawResult(rawResult);
      }
      setShowEditScenarioModal(false);
      setShowChangeImpactModal(true);
    },
    []
  );

  // ==== rMMM/HROAS only: the search/filter bar is pulled out of the header into its own row
  // above the chart bars (.sp-toolbar-row below) so it doesn't compete with the title and the
  // header's other dropdowns (Actualized/Absolute, on top of Sort by/Group by) for width at
  // narrow container sizes. GeoMMM has no Actualized/Absolute controls and no legend bar, so its
  // header was never busy — the search bar stays in its original right-side header position
  // (see the `!showRmmmControls && searchBar` line in rightActions below). ====
  const searchBar = (
    <div className='filter-bar-container'>
      <FilterBar
        lines={aggregatedBaselineData}
        onFilter={fn => setFilter({ filter: fn as (line: ScenarioDateRow) => boolean })}
        options={DIMENSIONS}
        size={Size.LG}
      />
    </div>
  );

  const rightActions = (
    <>
      {/* ==== GeoMMM only: keep the search bar in its original right-side header spot — see the
          searchBar comment above for why rMMM/HROAS render it in .sp-toolbar-row instead. ==== */}
      {!showRmmmControls && searchBar}
      {/* ==== HROAS (unmigrated): Actualized/Direct control also shown for HROAS ==== */}
      {showRmmmControls && (
        <Dropdown
          overlayPlacement={Placement.RIGHT}

          stretch={StretchMode.FIT}
          dropdownMenuClassName='sp-view-dropdown-menu'
          onChange={val => {
            const isActualized = val === 'Actualized';
            setShowActualized(isActualized);
            if (!isActualized) {
              handleCrossChannelChange(isActualized);
              setShowPercentageView(false);
            }
          }}
          options={[
            {
              label: 'Actualized',
              value: 'Actualized',
              className: 'sp-view-dropdown-item',
              trailingContent: (
                <div className='sp-view-description'>
                  Shows how channels interact with each other. Changes to one channel&rsquo;s spend
                  may shift predictions across others.
                </div>
              )
            },
            {
              label: 'Direct',
              value: 'Direct',
              className: 'sp-view-dropdown-item',
              trailingContent: (
                <div className='sp-view-description'>
                  Shows each channel&rsquo;s spend and effect in isolation. Changes to one channel
                  don&rsquo;t affect any others.
                </div>
              )
            }
          ]}
          size={Size.LG}
          value={showActualized ? 'Actualized' : 'Direct'}
        />
      )}
      {/* Absolute/Percentage control is always shown regardless of model type or Actualized/Direct
      — previously gated behind `showActualized` for rMMM/HROAS, which made it disappear on
      Direct. */}
      <Dropdown
        overlayPlacement={Placement.RIGHT}

        stretch={StretchMode.FIT}
        dropdownMenuClassName='sp-view-dropdown-menu'
        onChange={val => {
          const isPct = val === 'Percentage';
          setShowPercentageView(isPct);
          if (isPct && sortBy === CHART_SORT_BY_ENUM.REVENUE_WATERFALL) {
            setSortBy(CHART_SORT_BY_ENUM.ALPHABETICALLY);
          }
        }}
        options={[
          {
            label: 'Absolute',
            value: 'Absolute',
            className: 'sp-view-dropdown-item',
            trailingContent: (
              <div className='sp-view-description'>
                Shows absolute spend and revenue values for each channel.
              </div>
            )
          },
          {
            label: 'Percentage',
            value: 'Percentage',
            className: 'sp-view-dropdown-item',
            trailingContent: (
              <div className='sp-view-description'>
                Shows each channel&rsquo;s share of total spend and revenue as a percentage.
              </div>
            )
          }
        ]}
        size={Size.LG}
        value={showPercentageView ? 'Percentage' : 'Absolute'}
      />
      <Dropdown
        overlayPlacement={Placement.RIGHT}

        stretch={StretchMode.FIT}
        label='Sort by'
        onChange={val => {
          if (CHART_SORT_BY_OPTIONS.includes(val as CHART_SORT_BY_ENUM)) {
            setSortBy(val as CHART_SORT_BY_ENUM);
          } else {
            setSortDirection(val as SORT_DIRECTION_ENUM);
          }
        }}
        options={[
          ...CHART_SORT_BY_OPTIONS.filter(option => {
            if (showPercentageView && option === CHART_SORT_BY_ENUM.REVENUE_WATERFALL) {
              return false;
            }
            if (
              (option === CHART_SORT_BY_ENUM.REVENUE ||
                option === CHART_SORT_BY_ENUM.REVENUE_WATERFALL) &&
              !chartVariables.includes(SCENARIO_METRIC_ENUM.REVENUE)
            ) {
              return false;
            }
            if (
              option === CHART_SORT_BY_ENUM.ROAS &&
              !chartVariables.includes(SCENARIO_METRIC_ENUM.ROAS)
            ) {
              return false;
            }
            return true;
          }),
          ...SORT_DIRECTION_OPTIONS
        ].map(option => ({
          className:
            option === SORT_DIRECTION_ENUM.HIGH_TO_LOW ? 'dropdown-menu-item-border-top' : '',
          label:
            SORT_DIRECTION_LABEL_MAP[option as SORT_DIRECTION_ENUM] ??
            getChartSortOptionLabel(option as CHART_SORT_BY_ENUM, labelOutcomeVariable),
          value: option
        }))}
        size={Size.LG}
        value={`${sortBy} ${sortDirection}`}
      />
      <Dropdown
        overlayPlacement={Placement.RIGHT}

        stretch={StretchMode.FIT}
        label='Group by'
        onChange={setGroupBy}
        options={GROUP_BY_OPTIONS}
        size={Size.LG}
        value={groupBy}
      />
      <Button
        size={Size.LG}
        className='sp-edit-scenario-btn'
        data-testid='sp-edit-scenario-btn'
        leadingElement={<MdEdit />}
        onClick={() => {
          setIsFirstScenarioRun(!newScenarioTree);
          setOldScenarioSnapshot(newScenarioTree ?? scenarioRedshiftDataToTree(baselineData));
          setShowEditScenarioModal(true);
        }}
      />
    </>
  );

  if (runUnavailable) {
    return (
      <div className='scenario-planning'>
        <TableEmptyState message='No scenario model is available for the selected run.' />
      </div>
    );
  }

  return (
    <div className='scenario-planning'>
      {showChangeImpactModal && newScenarioTree && (
        <ChangeImpactModal
          isFirstRun={isFirstScenarioRun}
          modelType={modelType}
          oldScenario={oldScenarioSnapshot}
          newScenario={newScenarioTree}
          onBackToEditing={() => {
            setShowChangeImpactModal(false);
            setShowEditScenarioModal(true);
          }}
          onClose={() => setShowChangeImpactModal(false)}
          outcomeVariable={labelOutcomeVariable}
          // ==== HROAS: so the modal can re-derive weighted totals from
          // baselineByModel/previousByModel instead of the (unweighted) direct fields — see the
          // "Gap 2" fix in the PRD audit. undefined for non-HROAS pages, same no-op as elsewhere. ====
          hroasModels={isHroas ? hroasModels : undefined}
          hroasNotices={isHroas ? hroasAnalyzeNotices : undefined}
          hroasFailedModelIds={isHroas ? getHroasFailedModelIds(lastAnalyzeRawResult) : undefined}
          includedModelIds={isHroas ? includedModelIds : undefined}
          normalizedModelWeights={isHroas ? normalizedModelWeights : undefined}
        />
      )}
      {showEditScenarioModal && (
        <EditScenarioModal
          data={baselineData}
          end={end}
          isFirstRun={isFirstScenarioRun}
          modelConfig={modelConfig!}
          newScenarioTree={newScenarioTree as ScenarioNode[]}
          onApply={handleEditScenario}
          onHide={() => setShowEditScenarioModal(false)}
          outcomeVariable={labelOutcomeVariable}
          setNewScenarioTree={setNewScenarioTree}
          start={start}
          // ==== HROAS: same weighted-comparison fix as ChangeImpactModal, see above ====
          includedModelIds={isHroas ? includedModelIds : undefined}
          normalizedModelWeights={isHroas ? normalizedModelWeights : undefined}
        />
      )}
      {toolbar}
      <div className='content'>
        {(!rawBaselineData || isAnalyzing) && !hasNoModels && (
          <RunScenarioProgressDialog
            show
            percent={pageProgressPercent}
            solidBackdrop={!rawBaselineData}
            title={!rawBaselineData ? 'Loading Scenario Planner' : undefined}
            description={
              !rawBaselineData ? (
                <>
                  Gathering baseline model results.
                  <br />
                  This will take a minute.
                </>
              ) : undefined
            }
          />
        )}
        <div
          className='chart-container-sizer'
          data-testid='sp-chart'
          style={chartContainerMaxWidth ? { maxWidth: chartContainerMaxWidth } : undefined}
        >
          <ChartContainer
            viewRightActions
            rightActions={rightActions}
            title={
              <div className='sp-title-container'>
                Scenario
                {chartVariables.map((chartVar, i) => (
                  <Fragment key={chartVar}>
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      onChange={val => {
                        const newVars = [...chartVariables];
                        newVars[i] = val;
                        setChartVariables(newVars);
                        if (
                          (sortBy === CHART_SORT_BY_ENUM.REVENUE ||
                            sortBy === CHART_SORT_BY_ENUM.REVENUE_WATERFALL) &&
                          !newVars.includes(SCENARIO_METRIC_ENUM.REVENUE)
                        ) {
                          setSortBy(CHART_SORT_BY_ENUM.ALPHABETICALLY);
                        } else if (
                          sortBy === CHART_SORT_BY_ENUM.ROAS &&
                          !newVars.includes(SCENARIO_METRIC_ENUM.ROAS)
                        ) {
                          setSortBy(CHART_SORT_BY_ENUM.ALPHABETICALLY);
                        }
                      }}
                      options={chartVariableOptions.map(option => ({
                        label: getScenarioMetricLabel(option, labelOutcomeVariable),
                        value: option
                      }))}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={chartVar}
                    />
                    {i === 0 && 'and'}
                  </Fragment>
                ))}
              </div>
            }
          >
            {/* ==== HROAS: unified partial-instance warning — either the orchestrator skipped a
                model instance that errored (e.g. rMMM config/CSV misalignment), or an included
                model has zero rows in the selected date range (e.g. its data ends before the
                window starts) — both are the only signal that fewer than all instances actually
                contributed. One modal for both (auto-opens once when warnings first arrive,
                reopenable via the pill in the model-selection bar below) instead of a permanent
                banner or two separate dialogs. ==== */}
            {isHroas && hroasAllWarnings.length > 0 && (
              <AlertDialog
                intent={hroasFailureWarnings.length > 0 ? Intent.WARNING : Intent.INFO}
                show={showHroasWarningModal}
                onHide={closeHroasWarningModal}
                title={
                  hroasFailureWarnings.length > 0
                    ? 'Some models could not be included'
                    : 'Notes on this scenario'
                }
                className='sp-hroas-warning-dialog'
                body={
                  <>
                    {hroasFailureWarnings.map(renderHroasWarningRow)}
                    {hroasNoteWarnings.length > 0 && (
                      <>
                        {hroasFailureWarnings.length > 0 && (
                          <strong className='sp-hroas-warning-section'>
                            Notes on this scenario
                          </strong>
                        )}
                        {hroasNoteWarnings.map(renderHroasWarningRow)}
                      </>
                    )}
                  </>
                }
              />
            )}
            {/* ==== HROAS: model-selection bar — persistent include/exclude + weight per model
                instance, over the run's model slots (PRD §6.1). Changing either updates
                every number on the page instantly via the aggregatedBaselineData/
                currentAggregatedScenarioMap memos above — no lambda re-call. See
                ModelSelectionRow. ==== */}
            {/* ==== HROAS: model-selection row + legend, folded into one bordered section (the
                model row only exists for HROAS; the legend row is shared with rMMM). ==== */}
            {showRmmmControls && (
              <div className='sp-legend-bar'>
                {isHroas && (
                  <ModelSelectionRow
                    models={hroasModels}
                    segmentModelOrder={segmentsActive ? segmentModelOrder : undefined}
                    includedModelIds={includedModelIds}
                    setIncludedModelIds={setIncludedModelIds}
                    modelWeights={modelWeights}
                    warningCount={hroasAllWarnings.length}
                    onShowWarnings={openHroasWarningModal}
                    onEditWeights={() => setShowWeightingModal(true)}
                  />
                )}
                <div className='sp-legend-row'>
                  <div className='sp-legend-label'>Legend:</div>
                  {/* DC/DG is per-model (rMMM only) and has no meaning once models share a feature. */}
                  {!isHroas && (
                    <div className='sp-legend-group'>
                      {DC_DG_LEGEND_ITEMS.map(item => (
                        <LegendItem key={item.term} term={item.term} label={item.label}>
                          <DcDgBadge isDemandCapture={item.isDemandCapture} />
                        </LegendItem>
                      ))}
                    </div>
                  )}
                  {isHroas && (
                    <>
                      {segmentsActive && (
                        <ModelShadeLegend order={segmentModelOrder} models={hroasModels} />
                      )}
                      <div
                        title={
                          showActualized ? undefined : 'Switch to Actualized to split bars by model'
                        }
                      >
                        <ToggleSwitch
                          checked={segmentsActive}
                          design={Design.NEUTRAL}
                          disabled={!showActualized}
                          label='Segments by model'
                          labelPosition='leading'
                          onChange={() => handleModelSegmentsChange(!segmentsActive)}
                          size={Size.SM}
                        />
                      </div>
                    </>
                  )}
                  {showActualized && (
                    <>
                      {!isHroas && <div className='sp-legend-divider' />}
                      <LegendItem term='Actualized' label='Actualized'>
                        <div className='sp-legend-actualized-box' />
                      </LegendItem>
                      {showCrossChannelInteractions && (
                        <>
                          <LegendItem term='Added' label='Added'>
                            <div className='sp-legend-hatched-box'>
                              <ShadedPattern
                                color='white'
                                backgroundColor={Brand70}
                                id='legendAdded'
                              />
                            </div>
                          </LegendItem>
                          <LegendItem term='Reduced' label='Reduced'>
                            <div className='sp-legend-hatched-box'>
                              <ShadedPattern
                                color={Brand70}
                                backgroundColor='white'
                                id='legendReduced'
                              />
                            </div>
                          </LegendItem>
                        </>
                      )}
                      <ToggleSwitch
                        checked={showCrossChannelInteractions}
                        design={Design.NEUTRAL}
                        label='Cross-channel Interactions'
                        labelPosition='leading'
                        onChange={() => handleCrossChannelChange(!showCrossChannelInteractions)}
                        size={Size.SM}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
            {isHroas && (
              <EditModelWeightingModal
                show={showWeightingModal}
                onHide={() => setShowWeightingModal(false)}
                models={weightingModels}
                onSave={handleWeightingSave}
              />
            )}
            {showRmmmControls && <div className='sp-toolbar-row'>{searchBar}</div>}
            <div className='scenario-planning-charts-wrapper'>
              {canScrollCharts && (
                <div className='scroll-icons-column'>
                  {sortedBarChartData.map((_, idx) => (
                    <IconButton
                      key={idx}
                      className={cn('left-scroll-icon', scrollAtStart ? 'disabled' : '')}
                      appearance={Appearance.EMPTY}
                      onClick={handleChartScrollLeft}
                    >
                      <MdArrowForwardIos />
                    </IconButton>
                  ))}
                </div>
              )}
              <div className='charts-column' ref={chartsColumnRef} onScroll={handleChartsScroll}>
                {sortedBarChartData.map((data: ScenarioPlanningChartData[], idx: number) => (
                  <Fragment key={idx}>
                    <div className='scenario-planning-title-container'>
                      {getScenarioMetricLabel(chartVariables[idx], labelOutcomeVariable)}
                    </div>
                    <ScenarioPlanningChart
                      availableWidth={chartsColumnWidth}
                      channelColorMap={channelColorMap}
                      data={data}
                      isRmmm={modelType === MODEL_TYPES.R_MMM}
                      isWaterFall={sortBy === CHART_SORT_BY_ENUM.REVENUE_WATERFALL}
                      outcomeVariable={labelOutcomeVariable}
                      segmentModelOrder={segmentsActive ? segmentModelOrder : undefined}
                    />
                  </Fragment>
                ))}
              </div>
              {canScrollCharts && (
                <div className='scroll-icons-column'>
                  {sortedBarChartData.map((_, idx) => (
                    <IconButton
                      key={idx}
                      className={cn('right-scroll-icon', scrollAtEnd ? 'disabled' : '')}
                      appearance={Appearance.EMPTY}
                      onClick={handleChartScrollRight}
                    >
                      <MdArrowForwardIos />
                    </IconButton>
                  ))}
                </div>
              )}
            </div>
          </ChartContainer>
        </div>
        <ScenarioSparkCharts
          cards={sparkChartCards}
          hasScenario={Object.keys(currentAggregatedScenarioMap).length > 0}
          onEditScenario={() => {
            setIsFirstScenarioRun(!newScenarioTree);
            setOldScenarioSnapshot(newScenarioTree ?? scenarioRedshiftDataToTree(baselineData));
            setShowEditScenarioModal(true);
          }}
        />
      </div>
    </div>
  );
};

interface ModelSelectionRowProps {
  models: HroasSpModel[];
  // Set while segments are on: enabled models in stack order, for the shade swatches.
  segmentModelOrder?: string[];
  includedModelIds: Set<string>;
  setIncludedModelIds: Dispatch<SetStateAction<Set<string>>>;
  modelWeights: Record<string, number>;
  warningCount: number;
  onShowWarnings: () => void;
  onEditWeights: () => void;
}

// ==== HROAS: model-selection bar — persistent include/exclude + weight per model instance, over
// the run's model slots (PRD §6.1). Changing either updates every number on the page
// instantly via the aggregatedBaselineData/currentAggregatedScenarioMap memos in the parent — no
// lambda re-call. No dynamic model/run picker yet (deferred). Weight is edited via
// EditModelWeightingModal (the "control to open the weighting dialog" PRD §6.1 asks for), not
// typed in directly — the bar itself just shows the toggle and the current value. ====
const ModelSelectionRow = ({
  models,
  segmentModelOrder,
  includedModelIds,
  setIncludedModelIds,
  modelWeights,
  warningCount,
  onShowWarnings,
  onEditWeights
}: ModelSelectionRowProps) => (
  <div className='sp-model-selection-row'>
    <div className='sp-legend-label'>Models:</div>
    {warningCount > 0 && (
      <InteractiveSpan className='sp-hroas-warning-pill' onClick={onShowWarnings}>
        <MdWarning /> {warningCount}
      </InteractiveSpan>
    )}
    <div className='sp-model-selection-group'>
      {models.map(model => (
        <div key={model.id} className='sp-model-selection-item'>
          {segmentModelOrder && segmentModelOrder.includes(model.id) && (
            <span
              className='sp-model-shade-swatch'
              style={{
                backgroundColor: getModelShade(
                  Brand70,
                  segmentModelOrder.indexOf(model.id),
                  segmentModelOrder.length
                )
              }}
            />
          )}
          <ToggleSwitch
            label={model.label}
            checked={includedModelIds.has(model.id)}
            // Never allow the last included model to be toggled off — an empty includedModelIds
            // zeroes weighted revenue everywhere with no warning.
            disabled={includedModelIds.size === 1 && includedModelIds.has(model.id)}
            onChange={() =>
              setIncludedModelIds(prev => {
                const next = new Set(prev);
                if (next.has(model.id)) {
                  next.delete(model.id);
                } else {
                  next.add(model.id);
                }
                return next;
              })
            }
          />
          <span className='sp-model-weight-label'>
            Weight {(modelWeights[model.id] ?? 1).toFixed(2)}
          </span>
        </div>
      ))}
    </div>
    <IconButton
      className='sp-hroas-edit-weights-btn'
      appearance={Appearance.EMPTY}
      onClick={onEditWeights}
      title='Edit model weights'
    >
      <MdEdit />
    </IconButton>
  </div>
);

interface ModelShadeLegendProps {
  order: string[];
  models: HroasSpModel[];
}

// ==== HROAS: key for the per-model segments. Swatches use a Brand70 ramp as a stand-in for any
// channel color. ====
const ModelShadeLegend = ({ order, models }: ModelShadeLegendProps) => (
  <OverlayTrigger
    placement={Placement.BOTTOM}
    overlay={
      <Tooltip>
        Each bar is split by model. Segments are shades of that channel&apos;s color: the first
        model is darkest (bottom of the bar), the last is lightest.
      </Tooltip>
    }
  >
    <div className='sp-legend-item sp-legend-models'>
      {order.map((modelId, index) => (
        <span key={modelId} className='sp-legend-model'>
          <span
            className='sp-model-shade-swatch'
            style={{ backgroundColor: getModelShade(Brand70, index, order.length) }}
          />
          {models.find(m => m.id === modelId)?.label ?? modelId}
        </span>
      ))}
    </div>
  </OverlayTrigger>
);

interface LegendItemProps {
  term: string;
  label: string;
  children: ReactNode;
}

const LegendItem = ({ term, label, children }: LegendItemProps) => (
  <OverlayTrigger
    placement={Placement.BOTTOM}
    overlay={<Tooltip>{SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS[term]}</Tooltip>}
  >
    <div className='sp-legend-item'>
      {children}
      <div>{label}</div>
    </div>
  </OverlayTrigger>
);
